import React, { useEffect, useMemo, useState } from 'react'
import T from 'prop-types'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  OutlinedInput,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import config from 'config'
import logger from 'logger'

import SelectScrollable from 'components/atoms/SelectScrollable'

import useSnackbar from '../Snackbar/useSnackbar'

import { createGroups } from './RulesContent'

const GroupAssignmentDialog = ({ open, partner, handleClose, refetch }) => {
  const { t } = useTranslation()
  const [selectedGroup, setSelectedGroup] = useState('')
  const { showSnackbarMessage } = useSnackbar()

  const groups = useMemo(() => createGroups(t), [t])

  useEffect(() => {
    setSelectedGroup(partner?.groupId || '')
  }, [partner?.groupId])

  if (!open) return null

  const onSelectedGroupChange = (event) => {
    const {
      target: { value },
    } = event

    setSelectedGroup(value)
  }

  const onSubmit = async () => {
    if (selectedGroup !== '') {
      try {
        await axios.patch(
          `${config.apiBaseUrl}partners/${partner.partnerId}/group`,
          {
            groupId: selectedGroup,
          }
        )

        showSnackbarMessage({
          severity: 'success',
          message: t('rules.groupAssignmentDialog.success'),
        })
      } catch (e) {
        logger.error('[Error] - partner group assignment', e)
        showSnackbarMessage({
          severity: 'error',
          message: t('rules.groupAssignmentDialog.error'),
        })
      }

      refetch()
    }
    handleClose()
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t('rules.groupAssignmentDialog.title')}
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography>Partner:</Typography>
        <Typography variant="h6" gutterBottom>
          {partner.name}
        </Typography>
        <Typography>Group:</Typography>

        <SelectScrollable
          label="Group"
          displayEmpty
          value={selectedGroup}
          onChange={onSelectedGroupChange}
          input={<OutlinedInput />}
          data-testid="select-partner"
          renderValue={(selected) => {
            if (selected.length === 0) {
              return (
                <em>{t('rules.groupAssignmentDialog.selectPlaceholder')}</em>
              )
            }

            return (
              groups.find((group) => group.id === selected)?.name ||
              t('rules.groupAssignmentDialog.noGroup')
            )
          }}
        >
          <MenuItem disabled value="" data-testid="select-partner-disabled">
            <em>{t('rules.groupAssignmentDialog.selectPlaceholder')}</em>
          </MenuItem>
          <MenuItem value={0} data-testid="select-partner-nogroup">
            {t('rules.groupAssignmentDialog.noGroup')}
          </MenuItem>
          {groups.map((group) => (
            <MenuItem
              key={group.id}
              value={group.id}
              data-testid={`select-partner-${group.id}`}
            >
              {group.name}
            </MenuItem>
          ))}
        </SelectScrollable>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={onSubmit}
          data-testid="select-partner-button"
        >
          {t('rules.groupAssignmentDialog.saveLabel')}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

GroupAssignmentDialog.propTypes = {
  open: T.bool,
  partner: T.shape({
    groupId: T.number,
    name: T.string,
  }),
  handleClose: T.func,
  refetch: T.func,
}

export default GroupAssignmentDialog
